import { useEffect, useRef } from 'react';

import {
  failOperation,
  startOperation,
  succeedOperation,
} from '@mint-lib/datadog';

/**
 * Ends, in the activity step of the "New activity" modal, the
 * `activity.create` RUM operation that "New activity" (the planning header,
 * the empty state) starts before opening the modal.
 *
 * - `succeed` as soon as the activity exists. The flights step that follows
 *   edits an activity that is already created, so closing the modal there
 *   does not count as abandoned.
 * - `fail` when the activity could not be created, including the backend's
 *   name and date validation errors. Call `restartIfEnded` before every
 *   submit: after a failure it starts a new operation described as `retry`,
 *   so each attempt is measured on its own.
 * - Unmounting while the operation is open (Cancel, the close button, a
 *   click on the overlay) fails it as `abandoned`.
 *
 * Mirrors `usePageOperation` from `@mint-lib/datadog`.
 */
export const useActivityCreationOperation = () => {
  const isOpenRef = useRef(true);

  useEffect(() => {
    isOpenRef.current = true;

    return () => {
      if (isOpenRef.current) {
        isOpenRef.current = false;
        failOperation('activity.create', 'abandoned');
      }
    };
  }, []);

  const restartIfEnded = () => {
    if (!isOpenRef.current) {
      isOpenRef.current = true;
      startOperation('activity.create', { description: 'retry' });
    }
  };

  const succeed = (context: { hasBudget: boolean }) => {
    isOpenRef.current = false;
    succeedOperation('activity.create', { context });
  };

  const fail = (context: {
    reason: 'name' | 'dates' | 'no-activity' | 'request';
  }) => {
    isOpenRef.current = false;
    failOperation('activity.create', 'error', { context });
  };

  return { restartIfEnded, succeed, fail };
};
